import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, BookOpen, Search, MessageSquare, Wrench, Bookmark, Bell, Settings, FileText, Calculator, ListChecks, GitCompare, Workflow, Lightbulb, Sparkles, Columns, Scale, Zap, FolderOpen, Smartphone, WifiOff, Check, Download, Cloud } from 'lucide-react';
import BottomNav from '../components/BottomNav';

export default function HelpPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            How to Use LexProcure PH
          </h1>
          <p className="text-lg text-emerald-700 font-semibold">Your Complete Guide to the Philippine Procurement Law Digital Reference</p>
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Welcome to LexProcure PH</CardTitle>
            <CardDescription>
              LexProcure PH is your comprehensive digital platform for accessing Philippine government procurement laws, 
              implementing rules, and practical tools to help you navigate the procurement process.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Main Features */}
        <div className="space-y-6">
          {/* Library */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-emerald-600" />
                <CardTitle>Library</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-700">
                The Library is your central hub for accessing all legal documents related to Philippine procurement law. The fastest, most convenient way to access RA 12009, RA 9184, their IRRs, and curated cross-reference tools in one smart hub.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Scale className="w-5 h-5" />
                  Key References (with Scale Icons):
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                  <li><strong>RA 12009:</strong> New Government Procurement Act (25 Articles, 99 Sections) - <span className="text-emerald-600 font-semibold">Current</span></li>
                  <li><strong>RA 12009 IRR:</strong> Implementing Rules and Regulations (23 Rules, 116 Sections) - <span className="text-emerald-600 font-semibold">Current</span></li>
                  <li><strong>RA 9184:</strong> Government Procurement Reform Act - <span className="text-amber-600 font-semibold">Superseded</span></li>
                  <li><strong>RA 9184 IRR:</strong> Implementing Rules and Regulations (with Annexes & Appendices) - <span className="text-amber-600 font-semibold">Superseded</span></li>
                </ul>
                <p className="text-xs text-slate-600 mt-2">Each card displays complete information including Date Approved, Date Published, Date Effective, and Issuing Authority (e.g., Congress of the Philippines, Government Procurement Policy Board (GPPB)).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-purple-800 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Relevant GPPB Issuances:
                </h4>
                <ul className="list-disc list-inside space-y-2 text-sm text-slate-700">
                  <li><strong>GPPB Resolution No. 03-2025:</strong> Q&A on the Standard Forms for Procurement in accordance with RA 12009 and Its IRR - Provides guidance on the standard procurement forms including Philippine Bidding Documents (PBD), Strategic Planning Forms, and Bid Security requirements.</li>
                  <li><strong>GPPB Resolution No. 05-2025:</strong> Q&A on the Policy Guidance on Transitory Provisions of RA 12009 - Provides guidance on the transition from RA 9184 to the new procurement framework.</li>
                </ul>
                <p className="text-xs text-slate-600">Includes complete policy guidance with approval dates and issuing authority information.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                  <Columns className="w-5 h-5" />
                  Quick Reference Matrices:
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                  <li><strong>Section Titles:</strong> Side-by-side comparison showing provisions that are new, retained, amended, or deleted</li>
                  <li><strong>Parallel Provisions:</strong> Side-by-side comparison of parallel provisions under RA No. 12009 and RA No. 9184, highlighting similarities, refinements, and provisions that are newly introduced or repealed in the new law. <em>Viewable by all users; downloading is available for registered subscribers only.</em></li>
                  <li><strong>Procurement Timelines:</strong> Comparison of minimum and maximum calendar days for each procurement stage</li>
                  <li><strong>Standard Procurement Forms:</strong> A quick-reference matrix listing the standard procurement forms under RA 12009 and its IRR, with brief details and action points for Procuring Entities. <em>Viewable by all users; downloading is available for registered subscribers only.</em></li>
                </ul>
                <p className="text-xs text-slate-600 mt-2">Icons are displayed on top of titles for better visual organization.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  RA 9184 IRR Annexes & Appendices:
                </h4>
                <p className="text-sm text-slate-700">
                  At the end of the RA 9184 IRR Rules list, you'll find downloadable <strong>Annexes and Appendices</strong> (as of 19 July 2024) containing official forms, templates, and detailed procedures.
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                  <li>Click "View Online" to open PDFs in your browser</li>
                  <li>Click "Download PDF" to save to your device</li>
                </ul>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-emerald-800">How to Navigate:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                  <li>Click on any law or IRR card to view its contents</li>
                  <li>Each reference page displays: Effective Date and Status (Active/Superseded/Pending)</li>
                  <li>Browse through Articles (for laws) or Rules (for IRRs)</li>
                  <li>Click "View Sections" to see all sections under an Article/Rule</li>
                  <li>Click "View Content" on any section to see detailed provisions</li>
                  <li>Use "Read Full Text" to expand and view complete legal text</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Search */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Search className="w-6 h-6 text-emerald-600" />
                <CardTitle>Search Functionality</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-700">
                Quickly find specific sections, keywords, or topics across all legal documents.
              </p>
              <div className="bg-emerald-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-emerald-800">How to Search:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                  <li>Use the search bar at the top of the Library page</li>
                  <li>Type keywords, section numbers, or topics (e.g., "BAC", "competitive bidding")</li>
                  <li>View search results with highlighted matches</li>
                  <li>Click on results to navigate directly to that section</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Ask AI */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-emerald-600" />
                <CardTitle>Ask AI Assistant</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-700">
                Get instant answers to your procurement law questions using AI-powered assistance.
              </p>
              <div className="bg-emerald-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-emerald-800">How to Use:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                  <li>Navigate to "Ask AI" from the bottom navigation bar</li>
                  <li>Type your question in plain language</li>
                  <li>The AI will search through all legal documents and provide answers</li>
                  <li>Review cited sources and references provided with each answer</li>
                </ol>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Be specific with your questions for more accurate answers. 
                  For example: "What are the eligibility requirements for competitive bidding?"
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Zap Tools */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Wrench className="w-6 h-6 text-emerald-600" />
                <CardTitle>Zap Tools</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                Practical tools to assist with procurement planning and decision-making.
              </p>

              {/* Timeline Calculator */}
              <div className="border-l-4 border-emerald-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="w-5 h-5 text-emerald-600" />
                  <h4 className="font-semibold text-slate-800">1. Procurement Timeline Calculator</h4>
                </div>
                <p className="text-sm text-slate-700 mb-2">
                  Calculate all important dates for competitive bidding timelines.
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
                  <li>Select your start date from the calendar</li>
                  <li>View automatically calculated dates for all bidding activities</li>
                  <li>See advertisement, pre-bid, submission, and award dates</li>
                </ul>
              </div>

              {/* Section Equivalencies */}
              <div className="border-l-4 border-blue-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <GitCompare className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-slate-800">2. Section Equivalencies</h4>
                </div>
                <p className="text-sm text-slate-700 mb-2">
                  Find equivalent sections between RA 9184 and RA 12009.
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
                  <li>Search by section number or keyword</li>
                  <li>View side-by-side comparison of related sections</li>
                  <li>Understand changes between old and new procurement laws</li>
                </ul>
              </div>

              {/* Mode Selection Wizard */}
              <div className="border-l-4 border-purple-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <ListChecks className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-slate-800">3. Mode Selection Wizard (Enhanced)</h4>
                </div>
                <p className="text-sm text-slate-700 mb-2">
                  A comprehensive guided wizard using RA 12009 to help you determine the correct procurement mode for your project.
                </p>
                <div className="bg-purple-50 p-3 rounded-lg mb-2">
                  <p className="text-xs font-semibold text-purple-800 mb-2">7-Phase Decision Process</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs text-slate-700">
                    <li><strong>Project Name:</strong> Name your procurement project</li>
                    <li><strong>Entity Type:</strong> Select your organization type (NGA, LGU, GOCC, SUC, Other)</li>
                    <li><strong>Procurement Type:</strong> Choose manually OR use AI recommendation based on project description</li>
                    <li><strong>Contract Value:</strong> Enter your Approved Budget for Contract (ABC)</li>
                    <li><strong>Condition Assessment:</strong> Identify any special conditions (emergency, previous failures, etc.)</li>
                    <li><strong>Competitive Path:</strong> Market assessment questions</li>
                    <li><strong>Negotiated Path:</strong> 13 exception categories with threshold validation and justification checklist</li>
                  </ol>
                </div>
                <div className="bg-amber-50 p-2 rounded text-xs text-amber-800 flex items-start gap-2">
                  <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span><strong>AI Feature:</strong> Describe your project and let AI recommend whether it's Goods, Infrastructure, Consulting Services, or Mixed!</span>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 mt-2">
                  <li>Dynamic flow adjusts based on your selections</li>
                  <li>Receive specific mode recommendations with legal basis</li>
                  <li>View complete project profile in results</li>
                  <li>All based on current RA 12009 framework</li>
                </ul>
              </div>

              {/* End-to-End Process Flow */}
              <div className="border-l-4 border-orange-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <Workflow className="w-5 h-5 text-orange-600" />
                  <h4 className="font-semibold text-slate-800">4. End-to-End Process Flow</h4>
                </div>
                <p className="text-sm text-slate-700 mb-2">
                  View detailed step-by-step processes for each procurement mode.
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
                  <li>Select a procurement mode from the dropdown</li>
                  <li>See the complete process from budget approval to notice to proceed</li>
                  <li>Understand required documents and timelines for each step</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Workspace */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bookmark className="w-6 h-6 text-emerald-600" />
                <CardTitle>Workspace</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-700">
                Save and organize important sections for quick access and reference.
              </p>
              <div className="bg-emerald-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-emerald-800">How to Use Bookmarks:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                  <li>Click the bookmark icon when viewing any section</li>
                  <li>Access saved bookmarks from the Workspace page</li>
                  <li>Add personal notes to your bookmarked sections</li>
                  <li>Organize your frequently referenced provisions</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bell className="w-6 h-6 text-emerald-600" />
                <CardTitle>Notifications & Announcements</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-700">
                Stay updated with changes to procurement laws, system updates, and important announcements from the administrators.
              </p>
              <div className="bg-emerald-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-emerald-800">How to View Notifications:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                  <li>Click the <strong>bell icon</strong> in the navigation bar</li>
                  <li>A red badge shows the number of unread notifications</li>
                  <li>Click on a notification to read the full message</li>
                  <li>Notifications include system announcements, updates, and important notices</li>
                </ol>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-blue-800">Types of Notifications:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                  <li><strong>System Announcements:</strong> Important updates from administrators</li>
                  <li><strong>New Features:</strong> Updates about new tools and improvements</li>
                  <li><strong>Policy Updates:</strong> Changes to procurement laws and GPPB resolutions</li>
                  <li><strong>Maintenance Notices:</strong> Scheduled downtime or system updates</li>
                </ul>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Some announcements are also sent via email if enabled by the administrator. 
                  Check your inbox for important updates!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Password Reset */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings className="w-6 h-6 text-emerald-600" />
                <CardTitle>Forgot Password / Password Reset</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-700">
                If you've forgotten your password, you can easily reset it using the password reset feature.
              </p>
              <div className="bg-emerald-50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-emerald-800">How to Reset Your Password:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                  <li>Click <strong>"Forgot Password?"</strong> on the login page</li>
                  <li>Enter your registered email address</li>
                  <li>Check your email for a 6-digit reset code</li>
                  <li>Enter the code on the reset page</li>
                  <li>Create a new password (minimum 8 characters)</li>
                  <li>Log in with your new password</li>
                </ol>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> The reset code is valid for 24 hours. If you don't receive the email, 
                  check your spam folder or contact support.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings className="w-6 h-6 text-emerald-600" />
                <CardTitle>Settings</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-700">
                Customize your experience and manage your account preferences.
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                <li>Update your profile information</li>
                <li>Change password and security settings</li>
                <li>Manage notification preferences</li>
                <li>Toggle dark/light mode</li>
                <li>Adjust text size for better readability</li>
              </ul>
              
              <div className="bg-blue-50 p-4 rounded-lg space-y-2 mt-4">
                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  Session Management (2-Session Limit):
                </h4>
                <p className="text-sm text-slate-700">
                  Each account is limited to <strong>2 concurrent sessions</strong> for security. If you encounter a "Session limit exceeded" error:
                </p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700 pl-2">
                  <li>Go to <strong>Settings</strong> page</li>
                  <li>Scroll to <strong>"Sessions"</strong> section</li>
                  <li>Click <strong>"Clear All Sessions"</strong> button</li>
                  <li>Confirm the action</li>
                  <li><strong className="text-red-600">You will be logged out immediately</strong></li>
                  <li>Log back in</li>
                </ol>
                <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                  <p className="text-xs text-amber-900">
                    <strong>⚠️ Important:</strong> Clearing sessions logs you out from ALL sessions including your current one. You'll need to log in again.
                  </p>
                </div>
                <p className="text-xs text-slate-600 mt-2">
                  <strong>Use cases:</strong> Closed browser without logging out, switched browsers, can't access old sessions to log out manually.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Export & Download Features */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-emerald-600" />
                <CardTitle>Export & Download Features</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-700">
                Save and share legal provisions in multiple formats for offline reference or documentation.
              </p>
              <div className="bg-emerald-50 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-emerald-800">Available Export Options:</h4>
                <div className="space-y-3 text-sm">
                  <div className="border-l-4 border-blue-500 pl-3">
                    <p className="font-semibold text-slate-800">Export as Image</p>
                    <p className="text-slate-700">Creates a shareable image card with provision content</p>
                    <p className="text-slate-600 text-xs mt-1 flex items-center gap-1">
                      <FolderOpen className="w-3 h-3 flex-shrink-0" />
                      Saved to: Your browser's Downloads folder (e.g., RA-12009-Section-1.png)
                    </p>
                  </div>
                  <div className="border-l-4 border-red-500 pl-3">
                    <p className="font-semibold text-slate-800">Export as PDF</p>
                    <p className="text-slate-700">Opens print dialog - select "Save as PDF" option</p>
                    <p className="text-slate-600 text-xs mt-1 flex items-center gap-1">
                      <FolderOpen className="w-3 h-3 flex-shrink-0" />
                      You can choose where to save the PDF file
                    </p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-3">
                    <p className="font-semibold text-slate-800">Copy Text</p>
                    <p className="text-slate-700">Copies the provision text to your clipboard for pasting</p>
                    <p className="text-slate-600 text-xs mt-1 flex items-center gap-1">
                      <Lightbulb className="w-3 h-3 flex-shrink-0" />
                      Perfect for including in documents or emails
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Where are my downloads?</strong> All exported images are saved to your default Downloads folder. 
                  Check your browser settings to view or change the download location.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Tips & Best Practices */}
          <Card className="bg-gradient-to-r from-emerald-50 to-blue-50 border-emerald-200">
            <CardHeader>
              <CardTitle className="text-emerald-800">Tips & Best Practices</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span><strong>Bookmark frequently used sections</strong> for quick reference during procurement activities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span><strong>Use the Timeline Calculator</strong> at the start of each procurement to plan your schedule</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span><strong>Try the Mode Selection Wizard</strong> with AI recommendation if you're unsure which procurement method to use</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span><strong>Ask AI for clarifications</strong> when legal language is unclear or you need quick answers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span><strong>Check Section Equivalencies</strong> when transitioning from RA 9184 to RA 12009</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span><strong>Review the End-to-End Process Flow</strong> before starting a new procurement mode</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span><strong>Download RA 9184 IRR Annexes</strong> for official forms and templates you can use</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span><strong>Export provisions as images</strong> for easy sharing on social media or presentations</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Save to Home Screen */}
          <Card className="bg-gradient-to-r from-emerald-50 to-blue-50 border-2 border-emerald-300">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Smartphone className="w-6 h-6 text-emerald-600" />
                <CardTitle>Save to Home Screen</CardTitle>
              </div>
              <CardDescription>
                Add LexProcure PH to your device's home screen for instant access - works like a native app!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-white rounded-lg p-4 border-2 border-emerald-200">
                <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                  <Smartphone className="w-5 h-5" /> iOS (iPhone/iPad)
                </h4>
                <ol className="space-y-2 text-sm text-slate-700 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-emerald-600">1.</span>
                    <span>Open this website in <strong>Safari</strong> browser</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-emerald-600">2.</span>
                    <span>Tap the <strong>Share</strong> button (square with arrow pointing up) at the bottom of the screen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-emerald-600">3.</span>
                    <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-emerald-600">4.</span>
                    <span>Tap <strong>"Add"</strong> in the top right corner</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-600 font-semibold">Done! The LexProcure PH icon now appears on your home screen</span>
                  </li>
                </ol>
              </div>

              <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <Smartphone className="w-5 h-5" /> Android
                </h4>
                <ol className="space-y-2 text-sm text-slate-700 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-blue-600">1.</span>
                    <span>Open this website in <strong>Chrome</strong> browser</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-blue-600">2.</span>
                    <span>Tap the <strong>Menu</strong> button (three dots) in the top right corner</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-blue-600">3.</span>
                    <span>Tap <strong>"Add to Home screen"</strong> or <strong>"Install App"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-blue-600">4.</span>
                    <span>Tap <strong>"Add"</strong> or <strong>"Install"</strong> to confirm</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-600 font-semibold">Done! The LexProcure PH icon now appears on your home screen</span>
                  </li>
                </ol>
              </div>

              <div className="bg-emerald-100 rounded-lg p-4 border border-emerald-300">
                <h4 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  Benefits of Installing
                </h4>
                <ul className="space-y-1 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Faster access:</strong> Open directly from your home screen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Smartphone className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>App-like experience:</strong> Full-screen mode without browser UI</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <BookOpen className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Custom icon:</strong> Beautiful LexProcure PH logo on your device</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Better performance:</strong> Optimized for mobile devices</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Cloud className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Always updated:</strong> Automatically gets the latest version</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-xs text-blue-800 flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" />
                  <span><strong>Pro Tip:</strong> Once installed, LexProcure PH will work just like any other app on your phone. 
                  You can even organize it into folders and access it offline for saved bookmarks!</span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Support */}
          <Card className="bg-slate-100 border-slate-300">
            <CardHeader>
              <CardTitle>Need Additional Help?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4">
                If you have questions not covered in this guide or encounter any issues using LexProcure PH, 
                please contact our support team.
              </p>
              <div className="text-sm text-slate-600">
                <p><strong>Remember:</strong> LexProcure PH is continuously being updated with new features and content. 
                Check back regularly for the latest improvements and additions to the platform.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
